/** Presentation helpers for the existing project routes and historical evidence. */
export function growthActionLabel(action: string): string {
  const labels: Record<string, string> = {
    "Review the blocking decision and its evidence": "Examiner le blocage",
    "Resolve the conflicting evidence": "Résoudre les contradictions",
    "Examine identity evidence": "Confirmer l’identité",
    "Verify the current affiliation": "Confirmer le rôle actuel",
    "Verify startup eligibility": "Vérifier l’éligibilité de la société",
    "Research a professional email and record its source":
      "Rechercher un email professionnel",
    "Verify personal email attribution": "Confirmer à qui appartient l’email",
    "Research an attributable alternative email": "Rechercher un autre email",
    "Prepare a Bouncer verification for explicit budget review":
      "Préparer un contrôle Bouncer — autorisation requise",
    "Review fresh suppression and campaign history before any commercial use":
      "Vérifier les oppositions et l’historique commercial",
    "Review the remaining substantive evidence":
      "Compléter les preuves manquantes",
  };
  return labels[action] ?? action;
}

export function growthPriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    QUICK_WIN: "Vérification rapide",
    RESEARCH_REQUIRED: "Recherche nécessaire",
    PAID_VERIFICATION_POSSIBLE: "Contrôle payant possible",
    HUMAN_REVIEW: "Revue humaine",
    BLOCKED: "Bloquée",
  };
  return labels[priority] ?? priority;
}

export function growthCompanyPath(slug: string, companyId: number): string {
  if (!Number.isSafeInteger(companyId) || companyId <= 0)
    throw new RangeError("Invalid company ID");
  return `/projects/${encodeURIComponent(slug)}/companies/${companyId}`;
}

export function dateLabel(value: string | null | undefined) {
  if (!value) return "Non contrôlé";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(
        "fr-FR",
        /^\d{4}-\d{2}-\d{2}$/.test(value) ? { timeZone: "UTC" } : undefined,
      );
}
export function growthReason(code: string): string {
  const labels: [string, string][] = [
    ["HISTORICAL_EXCLUSION", "Exclusion historique conservée"],
    ["KNOWN_COMPANY_EXCLUSION", "Société exclue du ciblage"],
    ["KNOWN_COMMERCIAL_BLOCK", "Opposition ou blocage commercial connu"],
    [
      "EXCLUDED_INCUBATOR",
      "Incubateur exclu : " + code.split(":").slice(1).join(":"),
    ],
    [
      "ISSUER_CONFIRMED_Y_COMBINATOR",
      "Affiliation Y Combinator confirmée : acquisition bloquée",
    ],
    ["PREEXISTING_EXACT_TARGET_YC", "Exclusion Y Combinator déjà documentée"],
    [
      "PROVIDER_LATEST_SERIES_B",
      "Dernier financement Series B : acquisition bloquée",
    ],
    ["FUNDING_STAGE_OUTSIDE", "Stade de financement hors ciblage CFO"],
    ["EMAIL_MISSING", "Email professionnel manquant"],
    ["EMAIL_RISKY", "Email ACCEPT_ALL ou RISKY : alternative nécessaire"],
    ["IDENTITY_REVIEW", "Identité à confirmer"],
    ["AFFILIATION_REVIEW", "Rôle opérationnel actuel à confirmer"],
    ["ATTRIBUTION_REVIEW", "Attribution personnelle de l’email à confirmer"],
    ["ELIGIBILITY_REVIEW", "Éligibilité de la startup à réexaminer"],
    ["STALE_REVIEW", "Contrôle technique ancien ou absent"],
    ["SUPPRESSION", "Suppressions et historique commercial à actualiser"],
    ["LISAN_EXTERNAL", "Contrôle externe Lisan à actualiser"],
    ["BLACKLISTED_SECTOR", "Secteur d’activité et exclusions à documenter"],
    ["COMPLETED_FUNDING", "Financement effectivement réalisé à documenter"],
    ["COMPLETED_SEED", "Tour Seed ou Series A réalisé à confirmer"],
    ["CURRENT_FINANCE_TEAM", "Équipe finance actuelle à documenter"],
    ["FINANCE_TEAM_LEVEL", "Niveau de l’équipe finance à documenter"],
    [
      "FRENCH_PROFESSIONAL_LINK",
      "Lien professionnel avec la France à confirmer",
    ],
    ["INCUBATOR_RELATIONSHIPS", "Liens avec les incubateurs à examiner"],
    ["PROFESSIONAL_DOMAIN", "Domaine professionnel à corroborer"],
    ["US_TARGET", "Admissibilité au ciblage US à documenter"],
    [
      "DELIVERABILITY:",
      "Verdict technique : " + code.split(":").slice(1).join(":"),
    ],
  ];
  return labels.find(([prefix]) => code.startsWith(prefix))?.[1] ?? code;
}
export function isUsGrowthHistory(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  // The server reserves this marker and exposes only its safe summary publicly.
  return Boolean(metadata?.us_growth_history);
}

export function growthStatusLabel(value: string | null | undefined): string {
  const labels: Record<string, string> = {
    UNKNOWN: "Non renseigné",
    NOT_CHECKED: "Non contrôlé",
    UNRESOLVED: "À confirmer",
    CONFIRMED: "Confirmé",
    CONTRADICTED: "Preuves contradictoires",
    REVIEW: "À examiner",
    ADMISSIBLE: "Admissible sur les preuves historiques",
    BLOCKED: "Bloqué",
    NOT_ATTRIBUTED: "Attribution à confirmer",
    PROVIDER_VERIFIED: "Attribué par le fournisseur",
    CURRENT_FOUNDER_EXECUTIVE: "Fondateur opérationnel",
    CURRENT_FOUNDER_NON_EXECUTIVE: "Fondateur sans rôle opérationnel confirmé",
    CURRENT_OPERATIONAL_TARGET_ROLE_CONFIRMED: "Rôle opérationnel confirmé",
    OTHER_COMPANY_EXECUTIVE: "Dirigeant dans une autre entreprise",
    ACQUISITION_REQUIRES_CURRENT_TARGET_MANDATE_REVIEW:
      "Rôle à confirmer après acquisition",
    CROSS_CORROBORATED: "Attribution corroborée par plusieurs sources",
    DIRECT_PERSON_MATCH: "Correspondance directe avec la personne",
    UNATTRIBUTED: "Attribution non établie",
    INFERRED_PATTERN: "Adresse déduite d’un format, à confirmer",
    PUBLICLY_ATTRIBUTED: "Attribution documentée publiquement",
    FORMER_FOUNDER: "Ancien fondateur",
    SUPPRESSION_PENDING: "Contrôles commerciaux en attente",
    NOT_CLEARED: "Non levées",
    REFRESH_REQUIRED: "À actualiser",
    NONE_FOUND: "Aucun identifié",
    NONE: "Aucun",
    NOT_CHECKED_CURRENT: "À contrôler",
    seed: "Seed",
    series_a: "Series A",
    series_b: "Series B",
    early_stage: "Stade précoce, tour à préciser",
    later_stage: "Stade avancé",
  };
  return value ? (labels[value] ?? value) : "Non renseigné";
}
export function growthEmailLabel(verdict: string, fresh: boolean): string {
  if (verdict === "ACCEPT_ALL") return "ACCEPT_ALL · boîte non vérifiée";
  if (verdict === "RISKY") return "RISKY · adresse risquée";
  if (verdict === "DELIVERABLE")
    return fresh ? "DELIVERABLE" : "DELIVERABLE · contrôle ancien";
  return growthStatusLabel(verdict);
}

export function growthScoreRuleLabel(key: string, fallback: string): string {
  const labels: Record<string, string> = {
    need_window: "Stade de financement",
    existing_finance_team: "Équipe finance",
    missing_hr_lead: "Responsable RH",
    hiring_finance: "Recrutement finance",
    hiring_stratops: "Recrutement stratégie / opérations",
    hiring_hr: "Recrutement RH",
    funding_amount: "Montant levé et cohorte comparable",
    fte_growth: "Croissance des effectifs",
  };
  return labels[key] ?? fallback;
}
